/**
 * Verificación administrativa automática — reglas deterministas (Fase A).
 *
 * Resuelve los casos del catálogo de criterios que NO necesitan leer el archivo: los documentos que
 * no se aportaron y los formatos que genera la propia plataforma (declaración de residencia, carta
 * de conformación y relación de participantes), cuyo diligenciamiento y firma se saben mirando los
 * datos guardados.
 *
 * Los casos que exigen leer el PDF (legibilidad, vencimiento de la cédula, estado del RUT, fechas
 * del certificado) NO están aquí: van en la fase con Document Intelligence.
 *
 * Esto propone; nunca decide. El profesional confirma o corrige antes de que quede en firme.
 */

import { verificarDeclaracionResidencia } from './verificacionDeclaracion';

/** Textos oficiales de las observaciones. Si cambia la redacción, se corrige aquí. */
export const OBSERVACIONES: { [codigo: string]: string } = {
  a: 'No aportó el documento de identificación. Debe subsanar copia del documento de identificación vigente, por ambos lados y legible.',
  e: 'No aportó el RUT. Debe subsanar RUT del participante legible. No se acepta documento suspendido, en trámite, borrador ni con contraseña.',
  j: 'No se diligenció la declaración de residencia. Debe subsanar declaración de residencia en línea, debidamente diligenciada y firmada. La firma debe estar en formato png, jpg o jpeg.',
  k: 'La declaración de residencia no está firmada. Debe subsanar declaración de residencia en línea debidamente firmada. La firma debe estar en formato png, jpg o jpeg.',
  m: 'No se diligenció la carta de conformación de grupo. Debe subsanar carta de conformación de grupo en línea, debidamente diligenciada y firmada. La firma debe estar en formato png, jpg o jpeg.',
  n: 'La carta de conformación de grupo no está firmada. Debe subsanar carta de conformación de grupo en línea, debidamente diligenciada y firmada. La firma debe estar en formato png, jpg o jpeg.',
  o: 'La relación de participantes de la propuesta no está diligenciada. relación de participantes en línea, debidamente diligenciada y firmada. La firma debe estar en formato png, jpg o jpeg.',
  p: 'No aportó el certificado de existencia y representación legal. Debe subsanar certificado de existencia y representación legal de la entidad con fecha de expedición inferior 30 días calendario contados a partir de la fecha de apertura de la convocatoria.',
  r: 'No aportó Registro Único Nacional de Agentes Culturales - Soy Cultura. Debe subsanar copia del soporte del Registro Único Nacional de Agentes Culturales “Soy Cultura”. Este documento deberá ser presentado por todos los integrantes del grupo conformado, y podrá descargarse de la página web oficial del Ministerio: https://soycultura.mincultura.gov.co/',
};

export type TipoDocumento =
  | 'identificacion'
  | 'rut'
  | 'declaracion_residencia'
  | 'carta_grupo'
  | 'relacion_participantes'
  | 'certificado_existencia'
  | 'soy_cultura'
  /* Cualquier otro que pida la convocatoria y no este en el catalogo. */
  | 'otro';

/**
 * Los nombres de los documentos vienen de la tabla maestra y cambian entre convocatorias, así que se
 * emparejan por patrón y no por texto exacto. Si aparece un documento nuevo, se agrega aquí.
 */
const PATRONES: { tipo: TipoDocumento; patron: RegExp }[] = [
  { tipo: 'declaracion_residencia', patron: /declaracion de residencia/ },
  { tipo: 'carta_grupo', patron: /carta de conformacion/ },
  { tipo: 'relacion_participantes', patron: /relacion de participantes/ },
  { tipo: 'certificado_existencia', patron: /existencia y representacion/ },
  { tipo: 'soy_cultura', patron: /soy cultura|agentes culturales/ },
  { tipo: 'rut', patron: /\brut\b|registro unico tributario/ },
  { tipo: 'identificacion', patron: /cedula|documento de identi|identificacion|pasaporte/ },
];

/**
 * Para un documento que no esta en el catalogo no hay texto oficial, porque
 * el criterio se escribio documento por documento. Lo unico que se puede
 * afirmar sin abrirlo es que no se aporto, y asi se dice, con su nombre.
 */
export const observacionDeOtroDocumento = (nombre: string): string =>
  `No aportó ${nombre || 'el documento solicitado'}. Debe subsanarlo, completo y legible.`;

/** Formatos de firma que acepta el lineamiento. */
const FORMATOS_FIRMA = ['png', 'jpg', 'jpeg'];

/**
 * La carta de conformación la firman todos los integrantes del grupo. Si se decide que basta con la
 * firma del representante, se cambia a false.
 */
const EXIGE_FIRMA_DE_TODOS = true;

export type Hallazgo = {
  /** Posición del documento dentro de documentos_administrativos */
  indice: number;
  /**
   * Código del criterio en el catálogo (a, e, j, …), u "otro" cuando el
   * documento no está en el catálogo y solo se puede decir que falta.
   */
  codigo: string;
  tipo: TipoDocumento;
  /** Nombre del documento tal como viene en la postulación */
  nombre: string;
  estado: 'subsanar' | 'rechazar';
  observacion: string;
  /** 1 en las reglas deterministas: no hay lectura ni inferencia de por medio */
  confianza: number;
};

export type ResultadoVerificacion = {
  hallazgos: Hallazgo[];
  /** Documentos cuyo nombre no coincidió con ningún patrón: quedan para revisión manual */
  sinClasificar: { indice: number; nombre: string }[];
  /** Documentos que esta fase no alcanza a evaluar porque requieren leer el archivo */
  requierenLectura: { indice: number; nombre: string; tipo: TipoDocumento }[];
  /**
   * Documentos que quedaron completamente verificados: son formatos de la plataforma que cumplen
   * todo lo exigido y no tienen archivo adjunto, así que no queda nada por leer.
   */
  verificados: { indice: number; nombre: string; tipo: TipoDocumento }[];
  observacionPropuesta: string;
};

const sinTildes = (texto: any): string =>
  String(texto ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

export const clasificarDocumento = (nombre: any): TipoDocumento | null => {
  const limpio = sinTildes(nombre);
  return PATRONES.find((p) => p.patron.test(limpio))?.tipo ?? null;
};

const tieneTexto = (valor: any): boolean => String(valor ?? '').trim() !== '';

const tieneArchivo = (doc: any): boolean => tieneTexto(doc?.url_participante);

const firmaValida = (firma: any): boolean => {
  if (!tieneTexto(firma)) return false;
  const extension = String(firma).split('.').pop()?.toLowerCase() ?? '';
  return FORMATOS_FIRMA.includes(extension);
};

const participantes = (doc: any): any[] => (Array.isArray(doc?.participantes) ? doc.participantes : []);

const participantesDiligenciados = (doc: any): boolean =>
  participantes(doc).some((p: any) => tieneTexto(p?.nombresApellidos));

const participantesFirmados = (doc: any): boolean => {
  const lista = participantes(doc);
  if (lista.length === 0) return false;
  return EXIGE_FIRMA_DE_TODOS
    ? lista.every((p: any) => firmaValida(p?.firma))
    : firmaValida(lista[0]?.firma);
};

/**
 * La declaración de residencia se diligencia de dos formas distintas bajo el mismo nombre de
 * documento, según el tipo de participante:
 *
 * - Persona natural: llena sus propios datos (ciudad, dirección, años de residencia) y firma.
 * - Persona jurídica y grupo conformado: se llena la residencia de cada integrante, y firma cada uno.
 *
 * La plataforma las distingue por la presencia de participantes en el documento, igual que se hace
 * aquí. Si un grupo todavía no ha diligenciado nada, no habrá participantes y caerá en la
 * comprobación individual, que también dará "sin diligenciar": es el resultado correcto.
 */
const esDeclaracionDeGrupo = (doc: any): boolean => participantes(doc).length > 0;

const declaracionDiligenciada = (doc: any): boolean =>
  esDeclaracionDeGrupo(doc)
    ? participantesDiligenciados(doc)
    : tieneTexto(doc?.ciudad) && tieneTexto(doc?.direccion) && tieneTexto(doc?.anos_residencia);

/** En la declaración de grupo firma cada integrante; en la individual, el propio participante. */
const declaracionFirmada = (doc: any): boolean =>
  esDeclaracionDeGrupo(doc) ? participantesFirmados(doc) : firmaValida(doc?.firma_declaracion);

const hallazgo = (
  indice: number,
  codigo: string,
  tipo: TipoDocumento,
  nombre: string,
  /** Para los que no tienen texto oficial en el catálogo. */
  observacion = OBSERVACIONES[codigo]
): Hallazgo => ({
  indice,
  codigo,
  tipo,
  nombre,
  estado: 'subsanar',
  observacion,
  confianza: 1,
});

/**
 * Revisa los documentos administrativos de una postulación con las reglas que no necesitan abrir el
 * archivo. Solo evalúa los documentos que la postulación realmente exige: si un documento no está en
 * la lista, es porque no aplica para ese tipo de participante.
 */
export const verificarDocumentosAdministrativos = (documentosAdministrativos: any): ResultadoVerificacion => {
  const documentos = Array.isArray(documentosAdministrativos) ? documentosAdministrativos : [];
  const hallazgos: Hallazgo[] = [];
  const sinClasificar: { indice: number; nombre: string }[] = [];
  const requierenLectura: { indice: number; nombre: string; tipo: TipoDocumento }[] = [];
  const verificados: { indice: number; nombre: string; tipo: TipoDocumento }[] = [];

  documentos.forEach((doc: any, indice: number) => {
    const nombre = String(doc?.descripcion ?? doc?.tipo_documento ?? '').trim();
    const tipo = clasificarDocumento(nombre);

    /*
      El nombre no esta en el catalogo, asi que no se sabe que revisarle por
      dentro y queda para revision manual. Pero si ni siquiera se aporto, eso
      consta igual que en cualquier otro, y callarlo dejaba al participante
      viendo avisos de dos o tres documentos cuando le faltaban diez.
    */
    if (!tipo) {
      sinClasificar.push({ indice, nombre });
      if (!tieneArchivo(doc)) {
        hallazgos.push(hallazgo(indice, 'otro', 'otro', nombre, observacionDeOtroDocumento(nombre)));
      }
      return;
    }

    switch (tipo) {
      // Documentos que el participante carga: aquí solo se puede saber si los aportó o no.
      case 'identificacion':
      case 'rut':
      case 'certificado_existencia':
      case 'soy_cultura': {
        const codigos: { [k: string]: string } = {
          identificacion: 'a',
          rut: 'e',
          certificado_existencia: 'p',
          soy_cultura: 'r',
        };
        if (!tieneArchivo(doc)) {
          hallazgos.push(hallazgo(indice, codigos[tipo], tipo, nombre));
        } else {
          requierenLectura.push({ indice, nombre, tipo });
        }
        break;
      }

      // Formatos que genera la plataforma: el diligenciamiento y la firma están en los datos.
      case 'declaracion_residencia': {
        if (tieneArchivo(doc)) {
          requierenLectura.push({ indice, nombre, tipo });
        } else if (!declaracionDiligenciada(doc)) {
          hallazgos.push(hallazgo(indice, 'j', tipo, nombre));
        } else if (!declaracionFirmada(doc)) {
          hallazgos.push(hallazgo(indice, 'k', tipo, nombre));
        } else {
          // Está diligenciada y firmada: ahora se revisa que los datos cumplan el lineamiento.
          const porDatos = verificarDeclaracionResidencia(doc);
          if (porDatos.length === 0) {
            verificados.push({ indice, nombre, tipo });
          } else {
            porDatos.forEach((d) =>
              hallazgos.push({
                indice,
                codigo: d.codigo,
                tipo,
                nombre,
                estado: d.estado,
                observacion: d.observacion,
                confianza: d.confianza,
              })
            );
          }
        }
        break;
      }

      case 'carta_grupo': {
        if (tieneArchivo(doc)) {
          requierenLectura.push({ indice, nombre, tipo });
        } else if (!participantesDiligenciados(doc)) {
          hallazgos.push(hallazgo(indice, 'm', tipo, nombre));
        } else if (!participantesFirmados(doc)) {
          hallazgos.push(hallazgo(indice, 'n', tipo, nombre));
        } else {
          verificados.push({ indice, nombre, tipo });
        }
        break;
      }

      case 'relacion_participantes': {
        if (tieneArchivo(doc)) {
          requierenLectura.push({ indice, nombre, tipo });
        } else if (!participantesDiligenciados(doc)) {
          hallazgos.push(hallazgo(indice, 'o', tipo, nombre));
        } else {
          verificados.push({ indice, nombre, tipo });
        }
        break;
      }
    }
  });

  return {
    hallazgos,
    sinClasificar,
    requierenLectura,
    verificados,
    observacionPropuesta: componerObservacion(hallazgos),
  };
};

/**
 * Arma el texto para el campo de observaciones de la propuesta, un hallazgo por línea y en el orden
 * en que aparecen los documentos. En la primera verificación lo administrativo solo se subsana, así
 * que aquí no se redactan rechazos.
 */
export const componerObservacion = (hallazgos: Hallazgo[]): string =>
  hallazgos.map((h) => h.observacion).join('\n');

/** Etapa del proceso: en la primera lo administrativo se subsana; en la segunda ya se rechaza. */
export type EtapaVerificacion = 'primera' | 'segunda';

/**
 * Convierte la observación del primer informe en la del informe final.
 *
 * El lineamiento manda partir de lo que se pidió subsanar y reescribirlo empezando por "Se rechaza
 * ya que no subsanó…". Nunca puede quedar pidiendo que subsane: en esa etapa ya venció el plazo.
 *
 * La transformación toma lo que el texto original pedía aportar, que es lo que sigue a "Debe
 * subsanar". Si el texto no tiene esa forma, se antepone la frase sin recortar nada, para no perder
 * información por intentar reescribirla.
 */
export const comoRechazoPorNoSubsanar = (observacion: string): string => {
  const texto = String(observacion ?? '').trim();
  const marca = /debe subsanar\s+/i.exec(texto);

  if (!marca) return `Se rechaza ya que no subsanó. ${texto}`.trim();

  const desde = marca.index + marca[0].length;
  // Se queda con la frase de lo exigido; lo que viene después son condiciones del trámite.
  // Sin lookbehind a propósito: es ES2018 y Safari antiguo no lo parsea.
  const resto = texto.slice(desde);
  const finDeFrase = resto.indexOf('. ');
  const exigido = (finDeFrase >= 0 ? resto.slice(0, finDeFrase) : resto).replace(/\.$/, '').trim();

  return `Se rechaza ya que no subsanó ${exigido}.`;
};

/** Queda en los campos de auditoría que ya existen, donde iría el nombre del profesional. */
export const MARCADO_POR = 'Verificación automática';

/** Un documento lo decidió una persona si tiene marca y el autor no es la verificación automática. */
export const tieneDecisionHumana = (doc: any): boolean => {
  const autor = doc?.aceptado_por ?? doc?.actualizado_por ?? doc?.rechazado_por;
  const marcado = doc?.checkaceptar || doc?.checksubsanable || doc?.checkcancelar;
  return Boolean(marcado && tieneTexto(autor) && autor !== MARCADO_POR);
};

export type ResultadoAplicacion = {
  /** Copia de los documentos con las marcas puestas. No se modifica el arreglo original. */
  documentos: any[];
  observacion: string;
  subsanados: number;
  /** Documentos propuestos para rechazo: hoy, solo por incumplir el requisito de residencia. */
  rechazados: number;
  aceptados: number;
  /** Documentos que no se tocaron porque ya los había decidido una persona */
  respetados: number;
};

/**
 * Corre la verificación y deja las marcas sobre una copia de los documentos.
 *
 * Solo marca lo que puede sostener: subsanar cuando encontró un incumplimiento, y aceptar
 * únicamente en los formatos de la plataforma que quedaron completamente verificados. Lo que
 * necesita leer el archivo se deja intacto para el profesional.
 *
 * Nunca pisa una decisión humana previa, porque el botón se puede correr sobre una línea donde ya
 * hay propuestas revisadas a mano.
 */
export const aplicarVerificacion = (
  documentosAdministrativos: any,
  fecha: string,
  opciones: {
    respetarDecisionHumana?: boolean;
    hallazgosAdicionales?: Hallazgo[];
    etapa?: EtapaVerificacion;
  } = {}
): ResultadoAplicacion => {
  const { respetarDecisionHumana = true, hallazgosAdicionales = [], etapa = 'primera' } = opciones;
  const originales = Array.isArray(documentosAdministrativos) ? documentosAdministrativos : [];
  const resultado = verificarDocumentosAdministrativos(originales);

  // Los hallazgos de leer el documento se suman a los deterministas. No se pisan entre sí: un
  // documento con hallazgo determinista nunca llega a la fase de lectura.
  const encontrados = [...resultado.hallazgos, ...hallazgosAdicionales].sort(
    (a, b) => a.indice - b.indice
  );

  // En el informe final ya venció el plazo: lo que se había pedido subsanar pasa a ser rechazo, con
  // la observación reescrita. Lo que ya era rechazo se queda como está.
  const todosLosHallazgos: Hallazgo[] =
    etapa === 'segunda'
      ? encontrados.map((h) =>
          h.estado === 'subsanar'
            ? { ...h, estado: 'rechazar' as const, observacion: comoRechazoPorNoSubsanar(h.observacion) }
            : h
        )
      : encontrados;

  const documentos = originales.map((doc: any) => ({ ...doc }));
  let subsanados = 0;
  let rechazados = 0;
  let aceptados = 0;
  let respetados = 0;

  const marcables = (indice: number): boolean => {
    if (respetarDecisionHumana && tieneDecisionHumana(originales[indice])) {
      respetados++;
      return false;
    }
    return true;
  };

  // Un documento puede acumular varios hallazgos (por ejemplo, dirección incompleta y sin teléfono).
  // Se marca una sola vez, y si alguno es de rechazo, ese manda sobre los demás.
  const porDocumento = new Map<number, Hallazgo[]>();
  todosLosHallazgos.forEach((h) => {
    porDocumento.set(h.indice, [...(porDocumento.get(h.indice) ?? []), h]);
  });

  porDocumento.forEach((lista, indice) => {
    if (!marcables(indice)) return;

    const rechaza = lista.some((h) => h.estado === 'rechazar');
    const observacion = lista.map((h) => h.observacion).join('\n');
    const codigos = lista.map((h) => h.codigo).join(', ');
    const confianza = Math.min(...lista.map((h) => h.confianza));

    documentos[indice] = {
      ...documentos[indice],
      checksubsanable: !rechaza,
      checkcancelar: rechaza,
      checkaceptar: false,
      [rechaza ? 'rechazado_por' : 'actualizado_por']: MARCADO_POR,
      [rechaza ? 'fecha_rechazado' : 'fecha_subsanar']: fecha,
      ia_estado: rechaza ? 'rechazar' : 'subsanar',
      ia_codigo: codigos,
      ia_observacion: observacion,
      ia_confianza: confianza,
      ia_fecha: fecha,
    };

    if (rechaza) rechazados++;
    else subsanados++;
  });

  const observadosPorLectura = new Set(hallazgosAdicionales.map((h) => h.indice));

  resultado.verificados.forEach((v) => {
    if (observadosPorLectura.has(v.indice) || !marcables(v.indice)) return;
    documentos[v.indice] = {
      ...documentos[v.indice],
      checkaceptar: true,
      checksubsanable: false,
      checkcancelar: false,
      aceptado_por: MARCADO_POR,
      fecha_aceptado: fecha,
      ia_estado: 'aceptar',
      ia_confianza: 1,
      ia_fecha: fecha,
    };
    aceptados++;
  });

  return {
    documentos,
    observacion: componerObservacion(todosLosHallazgos),
    subsanados,
    rechazados,
    aceptados,
    respetados,
  };
};
